import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { DataNode } from 'antd/lib/tree';
import { Client } from '../../types/Client';
import ClientController from '../../controllers/client.controller';
import ClientFeatureConfigController from '../../controllers/clientFeatureConfig.controller';
import { ClientFeatureConfig } from '../../types/Feature';

type DataIndex = keyof Client;

export interface ClientCopyConfigContextInterface {
    loggedInClient: Client | undefined;
    selectedClients: React.Key[];
    clientConfigs: DataNode[] | undefined;
    checkedKeys: React.Key[];
    allClients: Client[] | undefined;
    searchedColumn: string;
    searchText: string;
    isLoadingLoggedInClient: boolean;
    isLoadingClientConfigs: boolean;
    isLoadingAllClients: boolean;
    isNoClientFound: boolean;
    isConfigCopyable: boolean;
    clientOffset: number;
    clientLimit: number;
    clientsCount: number;
    setClientOffset: (val: number) => void;
    setClientLimit: (val: number) => void;
    setSelectedClients: (val: React.Key[]) => void;
    setCheckedKeys: (val: React.Key[]) => void;
    setSearchedColumn: (val: DataIndex) => void;
    setSearchText: (val: string) => void;
}

export const ClientCopyConfigContext = createContext<ClientCopyConfigContextInterface | null>(null);

export const useClientCopyConfigContext = (): ClientCopyConfigContextInterface => {
    const ctx = useContext(ClientCopyConfigContext);

    if (!ctx) {
        throw new Error('Missing context');
    }
    return ctx;
};

export const ClientCopyConfigContextProvider = ({ children }: { children: ReactNode }): JSX.Element => {
    const [isLoadingLoggedInClient, setIsLoadingLoggedInClient] = useState<boolean>(false);
    const [isLoadingClientConfigs, setIsLoadingClientConfigs] = useState<boolean>(false);
    const [isLoadingAllClients, setIsLoadingAllClients] = useState<boolean>(false);
    const [isNoClientFound, setIsNoClientFound] = useState<boolean>(false);
    const [isConfigCopyable, setIsConfigCopyable] = useState<boolean>(false);
    const [loggedInClient, setLoggedInClient] = useState<Client | undefined>(undefined);
    const [selectedClients, setSelectedClients] = useState<React.Key[]>([]);
    const [clientConfigs, setClientConfigs] = useState<DataNode[] | undefined>(undefined);
    const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
    const [allClients, setAllClients] = useState<Client[] | undefined>(undefined);
    const [searchedColumn, setSearchedColumn] = useState('');
    const [searchText, setSearchText] = useState('');
    const [clientOffset, setClientOffset] = useState(0);
    const [clientLimit, setClientLimit] = useState(100);
    const [clientsCount, setClientsCount] = useState(0);

    const { clientId }: { clientId: string } = useParams();

    function clientConfigsToTree(list: ClientFeatureConfig<any>[]) {
        const tree: DataNode[] = [];
        list.forEach((clientFeatureConfig) => {
            const alreadyExistsingBranch: DataNode[] = tree.filter(
                (config) => config.key === `${clientFeatureConfig.featureId}`,
            );
            if (alreadyExistsingBranch.length > 0) {
                return alreadyExistsingBranch[0].children?.push({
                    title: JSON.stringify(clientFeatureConfig.config),
                    key: `${clientFeatureConfig.featureId}-${clientFeatureConfig.id}`,
                });
            }
            return tree.push({
                title: clientFeatureConfig.feature.name,
                key: `${clientFeatureConfig.featureId}`,
                children: [
                    {
                        title: JSON.stringify(clientFeatureConfig.config),
                        key: `${clientFeatureConfig.featureId}-${clientFeatureConfig.id}`,
                    },
                ],
            });
        });

        return tree;
    }

    const getLoggedInClient = async (cId: string | number): Promise<void> => {
        try {
            setIsLoadingLoggedInClient(true);
            const c = await ClientController.getClientById(Number(cId));
            setLoggedInClient(c);
            setIsNoClientFound(false);
        } catch (error) {
            setIsNoClientFound(true);
        } finally {
            setIsLoadingLoggedInClient(false);
        }
    };

    const loadClients = async (): Promise<void> => {
        try {
            setIsLoadingAllClients(true);
            const searchKeys: { [key: string]: string } = {
                name: 'client_name',
                stuartClientId: 'stuart_client_id',
                active: 'active',
            };
            const clientFilters =
                searchText && searchedColumn ? { [searchKeys[searchedColumn]]: searchText } : undefined;
            const { list, count } = await ClientController.getAllClients(
                clientOffset,
                clientOffset + clientLimit,
                clientFilters,
            );
            setClientsCount(count);
            setAllClients(list.filter((client) => client.id !== Number(clientId)));
        } catch (error) {
            setIsNoClientFound(true);
        } finally {
            setIsLoadingAllClients(false);
        }
    };

    const loadConfig = async (): Promise<void> => {
        try {
            setIsLoadingClientConfigs(true);
            const cc = await ClientFeatureConfigController.getAllFeatureConfigByClientId(Number(clientId));
            setClientConfigs(clientConfigsToTree(cc));
        } catch (error) {
            setIsNoClientFound(true);
        } finally {
            setIsLoadingClientConfigs(false);
        }
    };

    useEffect(() => {
        loadConfig();
    }, []);

    useEffect(() => {
        loadClients();
    }, [searchedColumn, searchText, clientOffset, clientLimit]);

    useEffect(() => {
        if (clientId) {
            getLoggedInClient(clientId);
        }
    }, [clientId]);

    useEffect(() => {
        if (checkedKeys.length > 0 && selectedClients.length > 0) {
            return setIsConfigCopyable(true);
        }
        return setIsConfigCopyable(false);
    }, [checkedKeys, selectedClients]);

    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const values = {
        /* Data */
        loggedInClient,
        selectedClients,
        clientConfigs,
        checkedKeys,
        allClients,
        searchedColumn,
        searchText,
        clientOffset,
        clientLimit,
        clientsCount,

        /* State */
        isLoadingLoggedInClient,
        isLoadingClientConfigs,
        isLoadingAllClients,
        isNoClientFound,
        isConfigCopyable,

        /* Setters */
        setSelectedClients,
        setCheckedKeys,
        setSearchedColumn,
        setSearchText,
        setClientOffset,
        setClientLimit,
    };

    return <ClientCopyConfigContext.Provider value={values}>{children}</ClientCopyConfigContext.Provider>;
};
